<script setup>
import { computed } from 'vue'
import berlinLocations from '~/data/berlin'
import HeroSection from '~/components/landing/HeroSection.vue'
import FeaturesSection from '~/components/landing/FeaturesSection.vue'
import GallerySection from '~/components/landing/GallerySection.vue'
import PackagesSection from '~/components/landing/PackagesSection.vue'
import FaqSection from '~/components/landing/FaqSection.vue'
import ContactForm from '~/components/landing/ContactForm.vue'
import ProcessSection from '~/components/landing/ProcessSection.vue'
import LocationSection from '~/components/landing/LocationSection.vue'
import SiteFooter from '~/components/SiteFooter.vue'
import { getDistrictEventTypes } from '~/data/districtEventTypes'

const pageTitle = 'Fotobox mieten in Berlin | Alle Stadtteile'
const pageDescription = 'Professionelle Fotobox für Ihre Veranstaltung in Berlin mieten. Verfügbar in allen Stadtteilen - Berlin Mitte, Kreuzberg, Charlottenburg und mehr.'

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogDescription: pageDescription,
  ogImage: 'https://mietfotobox.com/static/assets/hero.webp',
  canonical: 'https://mietfotobox.com/berlin/'
})

const districts = computed(() => {
  return Object.values(berlinLocations).map((location) => ({
    slug: location.district,
    name: location.name,
    description: location.description || `Fotobox mieten in ${location.name}`,
    venues: location.venues?.slice(0, 3) || [],
    eventTypes: getDistrictEventTypes('berlin')
  }))
})

const faqItems = [
  {
    question: 'In welchen Berliner Bezirken ist die Fotobox verfügbar?',
    answer: 'Wir liefern unsere Fotobox in die Berliner Bezirke aus, die auf dieser Seite aufgeführt sind, inklusive Aufbau und Abholung.'
  },
  {
    question: 'Kann ich direkt eine passende Eventseite für meinen Bezirk auswählen?',
    answer: 'Ja. Wählen Sie einfach Ihren Bezirk und danach den Anlass, zum Beispiel Hochzeit, Geburtstag oder Firmenfeier.'
  },
  {
    question: 'Ist die Anfahrt innerhalb Berlins inklusive?',
    answer: 'Die Anfahrt innerhalb unseres Berliner Einsatzgebiets ist bereits in der Planung berücksichtigt. Details stimmen wir im Angebot mit Ihnen ab.'
  },
  {
    question: 'Welche Leistungen sind bei der Miete enthalten?',
    answer: 'Je nach Paket erhalten Sie die Fotobox, Requisiten, Ausdrucke, digitale Galerie sowie Auf- und Abbau aus einer Hand.'
  }
]

const locationDescription = 'Unsere Fotoboxen sind in ganz Berlin verfügbar. Ob Innenstadt, Szeneviertel oder Randbezirk: Wir liefern, bauen auf und holen wieder ab, damit Ihre Veranstaltung reibungslos läuft.'
</script>

<template>
  <div>
    <HeroSection
      title="Fotobox mieten in Berlin"
      introText="Professionelle Fotoboxen für Veranstaltungen in ganz Berlin. Wählen Sie direkt den passenden Bezirk und den Anlass für Ihre Anfrage."
      heroImage="https://mietfotobox.com/static/assets/hero.webp"
    />

    <section id="districts" class="bg-white py-16">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <p class="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary">Berlin Übersicht</p>
          <h2 class="heading-2 mb-4">Bezirke und Eventarten in Berlin</h2>
        </div>

        <div class="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="district in districts"
            :key="district.slug"
            class="rounded-[2rem] border border-gray-200 bg-gray-50 p-6 shadow-sm transition-shadow hover:shadow-lg"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h3 class="mt-2 text-2xl font-semibold text-gray-900">{{ district.name }}</h3>
              </div>
              <NuxtLink
                :to="`/berlin/${district.slug}/`"
                class="rounded-full border border-primary/20 px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-white"
              >
                Zur Seite
              </NuxtLink>
            </div>

            <p class="mt-4 text-gray-600">{{ district.description }}</p>

            <div class="mt-6">
              <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Eventarten</h4>
              <div class="mt-3 grid gap-3">
                <NuxtLink
                  v-for="eventType in district.eventTypes"
                  :key="eventType.slug"
                  :to="`/berlin/${district.slug}/${eventType.slug}/`"
                  class="flex items-center justify-between rounded-2xl bg-white px-4 py-3 text-sm font-medium text-gray-800 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                >
                  <span class="flex items-center gap-3">
                    <span class="text-lg">{{ eventType.icon }}</span>
                    <span>{{ eventType.name }}</span>
                  </span>
                  <span class="text-primary">Ansehen</span>
                </NuxtLink>
              </div>
            </div>

            <div v-if="district.venues.length > 0" class="mt-6">
              <h4 class="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500">Beliebte Locations</h4>
              <ul class="mt-3 space-y-2 text-sm text-gray-600">
                <li v-for="venue in district.venues" :key="venue.name">{{ venue.name }}</li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

    <FeaturesSection locationName="Berlin" eventType="Veranstaltung" />
    <GallerySection locationName="Berlin" />
    <PackagesSection locationName="Berlin" />
    <div id="inquiry-form" class="bg-pink-700 px-4"></div>
    <ContactForm />
    <FaqSection :faq-items="faqItems" locationName="Berlin" />
    <ProcessSection locationName="Berlin" />
    <LocationSection locationName="Berlin" eventType="Veranstaltungen" :description="locationDescription" />
    <SiteFooter currentLocation="Berlin" />
  </div>
</template>
